import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../slices/userReducer";
import Icon from "./Icon";
import Logo from "../assets/22N.png";
import useUserRoleFlags from "../hooks/useUserRoleFlags";
import LanguageSwitcher from "./LanguageSwitcher";

/**
 * TopNavHorizontal — the mock's horizontal top nav with the "More" dropdown.
 * Used by the homepage header (HomeTopBanner) and by AuthorizedLayout on
 * every non-back-office page (the sidebar now only renders for admin chrome).
 * Role-gated items use the same checks as the Sidebar (shared hook).
 */
export default function TopNavHorizontal({ setLanguage, sticky = false }) {
  const user = useSelector(selectUser);
  const isLoggedIn = !!(user.id || user.token);
  const location = useLocation();
  const flags = useUserRoleFlags(user.id);
  const moreRef = useRef(null);
  const [moreOpen, setMoreOpen] = useState(false);

  // Close the More dropdown on outside click
  useEffect(() => {
    if (!moreOpen) return undefined;
    const handler = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [moreOpen]);

  const moreMenuItems = [
    { label: "Win tickets", to: "/spin-and-win" },
    { label: "Deals we want", to: "/deal-requests" },
    // Role-gated pages (same visibility rules as the Sidebar)
    ...(flags.isAdmin ? [{ label: "Families", to: "/families" }] : []),
    ...(flags.isPartner ? [{ label: "Partner dashboard", to: "/partner-dashboard" }] : []),
    ...(flags.isPromoter ? [{ label: "Promoter Dashboard", to: "/team-member" }] : []),
    ...(flags.isUGC ? [{ label: "UGC Dashboard", to: "/profile/ugc" }] : []),
    ...((flags.isAdmin || flags.isStaff) ? [{ label: "Admin", to: "/admin" }] : []),
  ];
  const hasMoreItems = moreMenuItems.length > 0;

  const NAV_LINKS = [
    { label: "Discover", to: "/" },
    { label: "Events", to: "/events" },
    { label: "Deals", to: "/lifestyle" },
    { label: "Community", to: "/community-chats" },
    { label: "Housemates", to: "/housemates" },
    { label: "Jobs", to: "/jobs" },
    { label: "Games", to: "/games" },
  ];

  const moreIsActive = moreMenuItems.some((item) =>
    location.pathname.startsWith(item.to)
  );

  return (
    <div
      className={`${sticky ? "sticky z-[900]" : ""} bg-[#121016] border-b border-white/[0.05]`}
      style={sticky ? { top: "var(--app-banner-height, 0px)" } : undefined}
    >
      <div className="flex justify-between items-center mx-auto px-[5%] py-[8px] w-full">
        <div className="flex justify-start items-center gap-[28px] h-[48px] min-w-0">
          <Link to="/" className="shrink-0">
            <img src={Logo} className="w-auto h-[30px]" alt="logo" />
          </Link>
          <nav aria-label="Main" className="hidden lg:flex items-center gap-[4px]">
            {NAV_LINKS.map((item) => {
              const isActive =
                item.to === "/"
                  ? location.pathname === "/"
                  : location.pathname.startsWith(item.to);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`relative px-[10px] py-[6px] text-[13px] font-semibold rounded-lg transition-colors whitespace-nowrap ${
                    isActive ? "text-white" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  {item.label}
                  {isActive && (
                    <span className="absolute inset-x-[10px] bottom-[2px] h-[2px] rounded-full bg-[#B98EF5]" />
                  )}
                </Link>
              );
            })}

            {/* More dropdown: extra pages + role-gated pages + language + socials */}
            {hasMoreItems && (
              <div ref={moreRef} className="relative">
                <button
                  type="button"
                  aria-expanded={moreOpen}
                  aria-haspopup="menu"
                  onClick={() => setMoreOpen((v) => !v)}
                  className={`relative flex items-center gap-1 px-[10px] py-[6px] text-[13px] font-semibold rounded-lg transition-colors ${
                    moreIsActive || moreOpen ? "text-white" : "text-neutral-400 hover:text-white"
                  }`}
                >
                  More
                  <Icon
                    icon={moreOpen ? "solar:alt-arrow-up-linear" : "solar:alt-arrow-down-linear"}
                    className="w-3.5 h-3.5"
                    width={14}
                  />
                  {moreIsActive && (
                    <span className="absolute inset-x-[10px] bottom-[2px] h-[2px] rounded-full bg-[#B98EF5]" />
                  )}
                </button>

                {moreOpen && (
                  <div
                    role="menu"
                    className="absolute right-0 top-[calc(100%+8px)] z-[1000] w-[220px] overflow-hidden rounded-xl border border-white/[0.1] bg-[#17131F] py-1.5 shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
                    style={{ backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)" }}
                  >
                    {moreMenuItems.map((item) => (
                      <Link
                        key={item.label}
                        to={item.to}
                        role="menuitem"
                        onClick={() => setMoreOpen(false)}
                        className={`block px-4 py-2 text-[13px] font-medium transition-colors ${
                          location.pathname.startsWith(item.to)
                            ? "bg-white/[0.06] text-white"
                            : "text-neutral-300 hover:bg-white/[0.05] hover:text-white"
                        }`}
                      >
                        {item.label}
                      </Link>
                    ))}

                    <div className="my-1.5 h-px bg-white/[0.08]" />

                    {/* Language */}
                    <div className="px-3 py-1">
                      <LanguageSwitcher setLanguage={setLanguage} variant="sidebar" />
                    </div>

                    <div className="my-1.5 h-px bg-white/[0.08]" />

                    {/* Socials */}
                    <a
                      href="https://www.instagram.com/22night.worldwide/"
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className="flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
                    >
                      <Icon icon="solar:camera-minimalistic-bold-duotone" className="w-4 h-4" width={16} />
                      Instagram
                    </a>
                    <a
                      href="https://www.tiktok.com/@22night.worldwide"
                      target="_blank"
                      rel="noopener noreferrer"
                      role="menuitem"
                      className="flex items-center gap-2.5 px-4 py-2 text-[13px] font-medium text-neutral-300 transition-colors hover:bg-white/[0.05] hover:text-white"
                    >
                      <Icon icon="solar:video-frame-play-horizontal-bold-duotone" className="w-4 h-4" width={16} />
                      Tiktok
                    </a>
                  </div>
                )}
              </div>
            )}
          </nav>
        </div>

        <div className="flex items-center gap-[12px] shrink-0">
          {isLoggedIn && (
            <Link
              to="/inbox"
              className="hidden lg:flex items-center justify-center w-[36px] h-[36px] rounded-full border border-white/10 bg-white/[0.05] text-neutral-300 hover:text-white hover:bg-white/[0.1] transition-colors"
              aria-label="Inbox"
            >
              <Icon icon="heroicons:chat-bubble-left-right" className="w-[18px] h-[18px]" />
            </Link>
          )}
          {isLoggedIn && (
            <Link
              to="/profile/me"
              className="hidden lg:flex items-center justify-center w-[36px] h-[36px] rounded-full border border-white/10 bg-white/[0.05] text-neutral-300 hover:text-white hover:bg-white/[0.1] transition-colors"
              aria-label="Account"
            >
              <Icon icon="solar:user-bold-duotone" className="w-[18px] h-[18px]" />
            </Link>
          )}
          {!isLoggedIn && (
            <Link
              to="/partners"
              className="bg-transparent hover:bg-[#EFEDF21A] px-[16px] !py-[6px] border border-[#EFEDF21A] rounded-[8px] text-[14px] text-white hover:text-white"
            >
              Partner with us
            </Link>
          )}
          <Link
            to={isLoggedIn ? "/events" : "/login"}
            className="rounded-lg bg-white px-4 py-2 text-[13px] font-bold text-[#120D19] hover:bg-white/90"
          >
            {isLoggedIn ? "View events" : "Join now"}
          </Link>
        </div>
      </div>
    </div>
  );
}
